import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendFormDataRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import {
  GET_USER_CONTRIBUTORS_QUERY_KEY,
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors.ts';

export const useUploadContributorProfilePhotoMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userContributorId, file }: { userContributorId: string; file: File }) => {
      const token = await getToken();
      return uploadContributorProfilePhoto({ userContributorId, file }, token ?? '');
    },
    onSuccess: async () => {
      // Invalidate and immediately refetch to get updated data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.refetchQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY], type: 'active' }),
        // Invalidate contributor summary query - invalidate all since userContributorId might not map directly to contributorId
        queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] }),
      ]);
    },
  });
};

const uploadContributorProfilePhoto = async (
  { userContributorId, file }: { userContributorId: string; file: File },
  token: string,
) => {
  const url = `${import.meta.env.VITE_API_URL}/user-contributors/${userContributorId}/profile-photo`;
  const formData = new FormData();
  formData.append('photo', file);
  return sendFormDataRequest({
    url,
    method: METHOD.POST,
    body: formData,
    token,
  });
};

export default useUploadContributorProfilePhotoMutation;
